import { useToggle } from 'usehooks-ts';
import { UserDevice } from '@mews-commander/data-access';
import { OverlayedLoader } from '@mews-commander/ui';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Card, Container, Divider, Stack } from '@optimus-web/core';
import { AuthorizedDevice } from './AuthorizedDevice';

interface AuthorizedDeviceListProps {
    isPending: boolean;
    devices: UserDevice[] | null | undefined;
    currentDeviceId?: string | null;
    onUserRemove: (deviceId: string) => void;
}

export function AuthorizedDeviceList({ devices, currentDeviceId, isPending, onUserRemove }: AuthorizedDeviceListProps) {
    const { hasMoreThanFiveDevices, toggleShowAllDevices, filteredDevices, showAllDevices } = useUserDevices(devices);
    return (
        <Card>
            <OverlayedLoader show={isPending} />
            <Container padding={['100', '0', '0', '0']}>
                <Stack as="ol" vertical>
                    {filteredDevices.map((device, index) => (
                        <Stack key={device.deviceId} as="li" vertical fill>
                            {index > 0 && <Divider />}
                            <AuthorizedDevice
                                device={device}
                                currentDeviceId={currentDeviceId}
                                onUserRemove={onUserRemove}
                            />
                        </Stack>
                    ))}
                </Stack>
                {hasMoreThanFiveDevices && !showAllDevices && (
                    <>
                        <Divider />
                        <Container padding={['200', '300']}>
                            <Button type="button" onClick={toggleShowAllDevices}>
                                <LocalizedText textKey={TextKey.AuthorizedDeviceShowAllDevices} />
                            </Button>
                        </Container>
                    </>
                )}
            </Container>
        </Card>
    );
}

function useUserDevices(unfilteredDevices: UserDevice[] | null | undefined) {
    const devices = unfilteredDevices || [];
    const hasMoreThanFiveDevices = devices.length > 5;
    const [showAllDevices, toggleShowAllDevices] = useToggle(false);
    const filteredDevices = devices.slice(0, showAllDevices ? undefined : 5);

    return {
        hasMoreThanFiveDevices,
        showAllDevices,
        toggleShowAllDevices,
        filteredDevices,
    };
}
